import axios from 'axios';
import { 
  API_BASE_URL, 
  API_CONFIG, 
  getTokenFromStorage,
  STORAGE_KEYS
} from '../constant/constant';

// Support API Endpoints
export const SUPPORT_ENDPOINTS = {
  // Admin endpoints
  ADMIN_TICKETS: '/admin/support/tickets',
  ADMIN_TICKET_DETAILS: (ticketId) => `/admin/support/tickets/${ticketId}`,
  ADMIN_RESPOND_TICKET: (ticketId) => `/admin/support/tickets/${ticketId}/respond`,
  ADMIN_UPDATE_STATUS: (ticketId) => `/admin/support/tickets/${ticketId}/status`,
  ADMIN_ASSIGN_TICKET: (ticketId) => `/admin/support/tickets/${ticketId}/assign`,
  ADMIN_DASHBOARD_STATS: '/admin/support/stats/dashboard',
  ADMIN_UNASSIGNED_TICKETS: '/admin/support/tickets/unassigned',
  ADMIN_MY_ASSIGNED_TICKETS: '/admin/support/tickets/my-assigned',
  
  // User endpoints
  USER_CREATE_TICKET: '/support/create',
  USER_TICKETS: '/support/tickets',
  USER_TICKET_DETAILS: (ticketId) => `/support/tickets/${ticketId}`,
  USER_RESPOND_TICKET: (ticketId) => `/support/tickets/${ticketId}/respond`
};

// Create axios instance with default config
const createSupportApi = () => {
  const supportApi = axios.create({
    baseURL: API_BASE_URL,
  });

  // Request interceptor to add auth token
  supportApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  supportApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, clear Redux Persist storage and redirect to login
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return supportApi;
};

const supportApi = createSupportApi();

// Helper function to handle API errors
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.msg || error.message || defaultMessage;
  throw new Error(errorMessage);
};

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  
  return queryParams.toString();
};

// Admin Support API Functions

// Get all support tickets (admin)
export const getAllTickets = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      search = '',
      status = '',
      priority = '',
      category = '',
      assignedTo = '',
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      search,
      status,
      priority,
      category,
      assignedTo,
      sortBy,
      sortOrder
    });

    const response = await supportApi.get(`${SUPPORT_ENDPOINTS.ADMIN_TICKETS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch support tickets');
  }
};

// Get specific ticket details (admin)
export const getTicketDetails = async (ticketId) => {
  try {
    const response = await supportApi.get(SUPPORT_ENDPOINTS.ADMIN_TICKET_DETAILS(ticketId));
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch ticket details');
  }
};

// Admin respond to ticket
export const respondToTicket = async (ticketId, responseData) => {
  try {
    const response = await supportApi.post(
      SUPPORT_ENDPOINTS.ADMIN_RESPOND_TICKET(ticketId), 
      responseData
    );
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to respond to ticket');
  }
};

// Update ticket status
export const updateTicketStatus = async (ticketId, status) => {
  try {
    const response = await supportApi.patch(
      SUPPORT_ENDPOINTS.ADMIN_UPDATE_STATUS(ticketId), 
      { status }
    );
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update ticket status');
  }
};

// Assign ticket to admin
export const assignTicket = async (ticketId, assignedTo) => {
  try {
    const response = await supportApi.patch(
      SUPPORT_ENDPOINTS.ADMIN_ASSIGN_TICKET(ticketId), 
      { assignedTo }
    );
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to assign ticket');
  }
};

// Get dashboard statistics
export const getDashboardStats = async (period = 30) => {
  try {
    const queryParams = buildQueryParams({ period });
    const response = await supportApi.get(`${SUPPORT_ENDPOINTS.ADMIN_DASHBOARD_STATS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch dashboard statistics');
  }
};

// Get unassigned tickets
export const getUnassignedTickets = async (limit = 50) => {
  try {
    const queryParams = buildQueryParams({ limit });
    const response = await supportApi.get(`${SUPPORT_ENDPOINTS.ADMIN_UNASSIGNED_TICKETS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch unassigned tickets');
  }
};

// Get tickets assigned to current admin
export const getMyAssignedTickets = async (limit = 50) => {
  try {
    const queryParams = buildQueryParams({ limit });
    const response = await supportApi.get(`${SUPPORT_ENDPOINTS.ADMIN_MY_ASSIGNED_TICKETS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch assigned tickets');
  }
};

// User Support API Functions

// Create a support ticket (user)
export const createTicket = async (ticketData) => {
  try {
    const response = await supportApi.post(SUPPORT_ENDPOINTS.USER_CREATE_TICKET, ticketData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create support ticket');
  }
};

// Get user's tickets
export const getUserTickets = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      status = '',
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      status,
      sortBy,
      sortOrder
    });

    const response = await supportApi.get(`${SUPPORT_ENDPOINTS.USER_TICKETS}?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch user tickets');
  }
};

// Get user ticket details
export const getUserTicketDetails = async (ticketId) => {
  try {
    const response = await supportApi.get(SUPPORT_ENDPOINTS.USER_TICKET_DETAILS(ticketId));
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch ticket details');
  }
};

// User respond to ticket
export const userRespondToTicket = async (ticketId, message) => {
  try {
    const response = await supportApi.post(
      SUPPORT_ENDPOINTS.USER_RESPOND_TICKET(ticketId), 
      { message }
    );
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to respond to ticket');
  }
};

// Feedback API Functions (no auth required)

// Get all feedbacks
export const getAllFeedbacks = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = API_CONFIG.DEFAULT_PAGE_SIZE,
      platform = '',
      api_endpoint = '',
      is_liked = '',
      userId = '',
      search = ''
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      platform,
      api_endpoint,
      is_liked,
      userId,
      search
    });

    // Create a simple axios instance without auth for feedback endpoint
    const response = await axios.get(`${API_BASE_URL}/feedback?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch feedbacks');
  }
};

// Export all functions as supportService object for backward compatibility
export const supportService = {
  // Admin functions
  getAllTickets,
  getTicketDetails,
  respondToTicket,
  updateTicketStatus,
  assignTicket,
  getDashboardStats,
  getUnassignedTickets,
  getMyAssignedTickets,
  
  // User functions
  createTicket,
  getUserTickets,
  getUserTicketDetails,
  userRespondToTicket,
  
  // Feedback functions
  getAllFeedbacks
};

export default supportService;
